import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import './Breadcrumb.scss';

/**
 * Breadcrumb Component with Schema.org markup
 * 
 * Usage:
 * <Breadcrumb items={[
 *   { name: 'Home', url: '/' },
 *   { name: 'Shop', url: '/shop' },
 *   { name: 'Product Name' }  // Last item without URL = current page
 * ]} />
 */
const Breadcrumb = ({ items }) => {
  // Generate schema.org BreadcrumbList
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      ...(item.url && { "item": `https://app.premierpharma.com${item.url}` })
    }))
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      
      <nav aria-label="breadcrumb" className="breadcrumb-nav">
        <ol className="breadcrumb">
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            
            return (
              <li 
                key={index} 
                className={`breadcrumb-item ${isLast ? 'active' : ''}`}
                {...(isLast && { 'aria-current': 'page' })}
              >
                {isLast || !item.url ? (
                  <span>{item.name}</span>
                ) : (
                  <Link to={item.url}>{item.name}</Link>
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

Breadcrumb.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      url: PropTypes.string
    })
  ).isRequired
};

export default Breadcrumb;
